import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ClientSidebar from '@/components/client/ClientSidebar';
import ChatArea from '@/components/client/ChatArea';
import UserPanel from '@/components/client/UserPanel';
import CreditsView from '@/components/client/CreditsView';
import HistoryView from '@/components/client/HistoryView';
import { LOGO_URL } from '@/data/mockData';
import { Menu, ArrowLeft, Settings, Play } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';

const APP_CREDIT_MESSAGES = [
  { role: 'assistant', text: `Here's how credits work:

\u2022 1 credit = 1 short video (10\u201315 seconds)
\u2022 2 credits = a longer video or 2 short ones
\u2022 4 credits = a full video or multiple smaller clips

You can use credits flexibly depending on what you need.` },
  { role: 'assistant', type: 'options', items: [
    { name: 'Quick Video', desc: '10\u201315 seconds \u2022 1 credit' },
    { name: 'Standard Video', desc: '20\u201330 seconds \u2022 2 credits' },
    { name: 'Full Video', desc: '50\u201360 seconds \u2022 4 credits' },
  ]},
  { role: 'assistant', text: "Delivery is typically 24\u201348 hours. Each project includes HD video, one revision round, and source files." },
  { role: 'assistant', text: "Need more credits? You can purchase them anytime from the web." },
];

function CreditsChat({ onClose }) {
  const [messages, setMessages] = useState(APP_CREDIT_MESSAGES);
  const [input, setInput] = useState('');
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const handleSend = () => {
    if (!input.trim()) return;
    setMessages(prev => [...prev, { role: 'user', text: input.trim() }]);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', text: "Great question! Credits give you full creative control over your video projects. You can combine them however works best for your needs. Want to start a new project?" }]);
    }, 600);
  };

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="credits-chat">
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex justify-center pl-12 md:pl-0 pointer-events-auto">
          <div className="glass-pill flex items-center gap-2.5 px-2 py-1.5 rounded-full">
            <button data-testid="credits-back" onClick={onClose} className="p-1.5 rounded-full hover:bg-black/[0.04] transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-[13px] font-medium text-gray-900 font-['Outfit'] pr-3">Credits</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 pt-16">
        <div className="max-w-lg mx-auto w-full px-4 sm:px-6 py-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 mt-0.5">
                  <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                </div>
              )}
              {msg.type === 'options' ? (
                <div className="flex flex-col gap-1.5 max-w-[85%]">
                  {msg.items.map((item, j) => (
                    <div key={j} className="border border-gray-200/50 rounded-xl px-4 py-2.5 bg-white/25">
                      <span className="text-sm font-medium text-gray-900 block">{item.name}</span>
                      <span className="text-[11px] text-gray-400">{item.desc}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-sm leading-relaxed max-w-[85%] whitespace-pre-line ${msg.role === 'user' ? 'text-gray-800' : 'text-gray-500'}`}>
                  {msg.text}
                </p>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      <div className="shrink-0 px-4 pb-[env(safe-area-inset-bottom,6px)] pt-1.5 pointer-events-none">
        <div className="max-w-lg mx-auto pointer-events-auto">
          <div className="glass-pill flex items-center gap-1 rounded-full px-2 py-1">
            <textarea
              ref={textareaRef}
              data-testid="credits-input"
              value={input}
              onChange={(e) => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about credits, delivery, or video options..."
              className="flex-1 bg-transparent text-gray-900 text-[14px] resize-none outline-none px-2 py-1.5 leading-5 placeholder:text-gray-400"
              rows={1}
              style={{ maxHeight: '120px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsChat({ onClose, onEditProfile }) {
  const { isLoggedIn, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "How can I help you with your account?" },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const pushAssistant = (text) =>
    setMessages(prev => [...prev, { role: 'assistant', text }]);

  const handleAction = async (id) => {
    if (id === 'profile') {
      // Real action: open the profile editor in the user panel
      onEditProfile?.();
      return;
    }
    if (id === 'help') {
      pushAssistant(
        isLoggedIn
          ? `I'm here to help, ${user?.email}. Ask anything about your videos, credits, delivery, or account — your message goes straight to our team.`
          : "I'm here to help! Ask anything about your videos, credits, delivery, or account."
      );
      textareaRef.current?.focus();
      return;
    }
    if (id === 'privacy') {
      pushAssistant(
        "Here's how your data is handled at Ruido:\n\n" +
        "• Account data (email, profile, credits) lives in our private Supabase backend.\n" +
        "• Project files and messages are visible only to you and the production team assigned to your project.\n" +
        "• Generated AI assets stay attached to your project and are never reused for other clients.\n" +
        "• You can request a full export or permanent deletion of your data anytime — just type \"delete my account\" below."
      );
      textareaRef.current?.focus();
      return;
    }
    if (id === 'logout') {
      if (busy) return;
      setBusy(true);
      pushAssistant("Signing you out…");
      try {
        await signOut();
      } catch (e) {
        // continue regardless
      }
      // Clear any locally cached production refs; AppContext rehydrates on auth change
      try { localStorage.removeItem('ruido_project_references_v1'); } catch { /* noop */ }
      navigate('/', { replace: true });
    }
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const text = input.trim();
    setMessages(prev => [...prev, { role: 'user', text }]);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    // Quick intent detection for delete
    if (/delete\s+my\s+account|borrar\s+mi\s+cuenta/i.test(text)) {
      setTimeout(() => pushAssistant(
        "Account deletion is handled by our team for safety. We'll email you within 24h to confirm. " +
        "If you want to log out for now, tap Log out below."
      ), 500);
      return;
    }
    setTimeout(() => {
      pushAssistant("Got it! We'll take care of that. Anything else?");
    }, 600);
  };

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="settings-chat">
      {/* Header — same style as Credits */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex justify-center pl-12 md:pl-0 pointer-events-auto">
          <div className="glass-pill flex items-center gap-2.5 px-2 py-1.5 rounded-full">
            <button data-testid="settings-back" onClick={onClose} className="p-1.5 rounded-full hover:bg-black/[0.04] transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-[13px] font-medium text-gray-900 font-['Outfit'] pr-3">Settings</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 pt-16">
        <div className="max-w-lg mx-auto w-full px-4 sm:px-6 py-4">
          {/* Chat messages */}
          <div className="space-y-4 mb-6">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 mt-0.5">
                    <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                  </div>
                )}
                <p className={`text-sm leading-relaxed max-w-[85%] ${msg.role === 'user' ? 'text-gray-800' : 'text-gray-500'}`}>
                  {msg.text}
                </p>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Action buttons — bigger, more spacing */}
          <div className="space-y-2.5 mt-4">
            {[
              { label: 'Edit profile', id: 'profile' },
              { label: 'Help & support', id: 'help' },
              { label: 'Privacy', id: 'privacy' },
              ...(isLoggedIn ? [{ label: 'Log out', id: 'logout' }] : []),
            ].map((act) => (
              <button
                key={act.id}
                data-testid={`setting-${act.id}`}
                onClick={() => handleAction(act.id)}
                disabled={busy && act.id === 'logout'}
                className={`w-full py-3 px-5 bg-white/40 border border-gray-200/50 rounded-2xl text-sm hover:bg-white/60 transition-all active:scale-[0.98] text-left ${
                  act.id === 'logout' ? 'text-rose-500 hover:text-rose-600' : 'text-gray-600 hover:text-gray-900'
                } disabled:opacity-50`}
              >
                {act.label}
              </button>
            ))}
          </div>
        </div>
      </ScrollArea>

      <div className="shrink-0 px-4 pb-[env(safe-area-inset-bottom,6px)] pt-1.5 pointer-events-none">
        <div className="max-w-lg mx-auto pointer-events-auto">
          <div className="glass-pill flex items-center gap-1 rounded-full px-2 py-1">
            <textarea
              ref={textareaRef}
              data-testid="settings-input"
              value={input}
              onChange={(e) => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Need help or want to change something?"
              className="flex-1 bg-transparent text-gray-900 text-[14px] resize-none outline-none px-2 py-1.5 leading-5 placeholder:text-gray-400"
              rows={1}
              style={{ maxHeight: '120px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientView() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [creditsOrigin, setCreditsOrigin] = useState(null);
  const [autoEditProfile, setAutoEditProfile] = useState(false);

  const handleShowUserPanel = () => {
    setActivePanel('user');
    setAutoEditProfile(false);
    setSidebarOpen(false);
  };

  const handleShowSettings = () => {
    setActivePanel('settings');
    setSidebarOpen(false);
  };

  const handleEditProfileFromSettings = () => {
    setAutoEditProfile(true);
    setActivePanel('user');
  };

  const handleShowCreditsFromAccount = () => {
    setCreditsOrigin('account');
    setActivePanel('credits');
    setSidebarOpen(false);
  };

  const handleShowCreditsPublic = () => {
    setCreditsOrigin('public');
    setActivePanel('credits');
    setSidebarOpen(false);
  };

  const handleShowHistory = () => {
    setActivePanel('history');
    setSidebarOpen(false);
  };

  const handleClosePanel = () => setActivePanel(null);
  const handleBackToUser = () => setActivePanel('user');

  const handleCreditsBack = () => {
    if (creditsOrigin === 'account') {
      setActivePanel('user');
    } else {
      setActivePanel(null);
    }
    setCreditsOrigin(null);
  };

  const sidebarProps = {
    onSelect: () => { setSidebarOpen(false); setActivePanel(null); },
    onShowUserPanel: handleShowUserPanel,
    onShowCredits: handleShowCreditsPublic,
    onShowHistory: handleShowHistory,
  };

  return (
    <div className="flex h-screen h-screen-mobile bg-[#f0efed]" data-testid="client-view">
      <div className="hidden md:flex w-64 border-r border-gray-200/60 flex-col shrink-0 bg-[#f7f6f4]">
        <ClientSidebar {...sidebarProps} />
      </div>

      <div className="md:hidden">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-btn"
              className="fixed top-4 left-4 z-50 p-2.5 glass-pill rounded-full transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-500" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 p-0 bg-[#f7f6f4] border-gray-200/60">
            <ClientSidebar {...sidebarProps} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        {activePanel === 'user' ? (
          <UserPanel onClose={handleClosePanel} onShowSettings={handleShowSettings} onShowCredits={handleShowCreditsFromAccount} autoEdit={autoEditProfile} />
        ) : activePanel === 'settings' ? (
          <SettingsChat onClose={handleBackToUser} onEditProfile={handleEditProfileFromSettings} />
        ) : activePanel === 'credits' ? (
          <CreditsView onClose={handleCreditsBack} />
        ) : activePanel === 'history' ? (
          <HistoryView onClose={handleClosePanel} />
        ) : (
          <ChatArea />
        )}
      </div>
    </div>
  );
}
